import { Box, Stack } from "@mui/material"
import { Control, UseFormSetValue, UseFormWatch } from "react-hook-form"
import { TSale } from "../../../../core/types/Sale"
import TextFieldRequired from "../../../Inputs/TextFieldRequired"
import Validations from "../../../../core/functions/validations"
import { useMemo, useEffect } from "react"
import { formatAsYouType, isValidPhone, ensurePhoneEngine } from "../../../../core/phone"
import { useBuyerCountryPhoneSync } from "../../../../core/hooks/useBuyerCountryPhoneSync"
import {
  CountrySelect,
  COUNTRY_SELECT_LABEL_OFFSET,
} from "../../../Inputs/CountrySelect"
import { useCheckoutContext } from "../../../../core/hooks/useCheckoutContext"
import CheckoutFunctions from "../../../../core/functions/CheckoutFunctions"
import { useGatewayContext } from "../../../../core/contexts/GatewayContext"
import { useTranslation } from "react-i18next"

export interface ICheckoutClient4 {
  control: Control<TSale, any>
  setValue: UseFormSetValue<TSale>
  watch: UseFormWatch<TSale>
  saveLostSale: () => void
  clearErrors: () => void
}

const CheckoutClient4 = ({
  control,
  setValue,
  watch,
  saveLostSale,
  clearErrors,
}: ICheckoutClient4) => {
  const { productPrice } = useCheckoutContext()
  const { useStripe: useStripePayment } = useGatewayContext()
  const { t } = useTranslation()

  useBuyerCountryPhoneSync(watch, setValue)

  const emailValue = watch("email")
  const phoneValue = watch("phone")

  const phoneCountry = watch("phoneCountry") ?? "BR"

  // Carrega o motor de numeração quando o país deixa de ser BR.
  // BR nunca carrega — máscara síncrona própria.
  useEffect(() => {
    ensurePhoneEngine(phoneCountry)
  }, [phoneCountry])

  const values = useMemo(
    () => ({
      customFields: CheckoutFunctions.getCustomFields(productPrice),
      helperText: CheckoutFunctions.getHelperText(productPrice, t),
    }),
    [productPrice, t]
  )

  return (
    <Stack direction="column" spacing={1.5}>
      <TextFieldRequired
        control={control}
        fieldName="name"
        label={t("checkout.client.name")}
        placeholder={t("checkout.client.namePlaceholder")}
        onFieldExit={saveLostSale}
        clearErrors={clearErrors}
        inputProps={{
          "data-cy": "customerName",
          "data-testid": "customerName",
          autoComplete: "name",
        }}
        dataCyErrorLabel="customerNameError"
        transform={(v) => v.replace(/[0-9]/g, "")}
        validate={(v) => {
          const s = String(v ?? "")
          return /\d/.test(s) ? t("checkout.client.nameNoNumbers") : true
        }}
      />
      <TextFieldRequired
        control={control}
        fieldName="email"
        label={t("checkout.client.email")}
        placeholder={t("checkout.client.emailPlaceholder")}
        helperText={values.helperText.email}
        onFieldExit={saveLostSale}
        clearErrors={clearErrors}
        validate={(value) =>
          !value || value == "" ? true : Validations.email(value)
        }
        inputProps={{
          "data-cy": "customerEmail",
          "data-testid": "customerEmail",
          style: { textTransform: "lowercase" },
        }}
        dataCyErrorLabel="customerEmailError"
      />
      <TextFieldRequired
        control={control}
        fieldName="emailConfirm"
        label={t("checkout.client.emailConfirm")}
        placeholder={t("checkout.client.emailConfirmPlaceholder")}
        clearErrors={clearErrors}
        validate={(value) =>
          !value || value == ""
            ? true
            : Validations.email(value) &&
              ((value as string) ?? "").toLowerCase() ===
                emailValue.toLowerCase()
        }
        validateLabel={
          !Validations.email(watch("emailConfirm"))
            ? t("checkout.client.emailConfirmInvalid")
            : t("checkout.client.emailConfirmMismatch")
        }
        inputProps={{
          "data-cy": "customerEmailConfirm",
          "data-testid": "customerEmailConfirm",
          style: { textTransform: "lowercase" },
        }}
        dataCyErrorLabel="customerEmailConfirmError"
      />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <TextFieldRequired
          control={control}
          fieldName="phone"
          label={t("checkout.client.phone")}
          helperText={values.helperText.phone}
          shrink={phoneValue !== ""}
          inputProps={{
            inputMode: "numeric",
            "data-cy": "customerPhone",
            "data-testid": "customerPhone",
          }}
          sxInputLabel={{
            ml: phoneValue === "" ? COUNTRY_SELECT_LABEL_OFFSET : 3.5,
          }}
          dataCyErrorLabel="customerPhoneError"
          onFieldExit={saveLostSale}
          clearErrors={clearErrors}
          transform={(v: string) => formatAsYouType(v, phoneCountry)}
          validate={(value) =>
            !value || value === "" ? true : isValidPhone(value, phoneCountry)
          }
          startAdornment={
            <CountrySelect
              value={phoneCountry}
              onChange={(iso) => {
                setValue("phoneCountry", iso)
                setValue("phone", "")
              }}
            />
          }
        />

        <Box sx={{ display: { xs: "block", sm: "none" } }} />

        {!useStripePayment && (
          <TextFieldRequired
            control={control}
            fieldName="document"
            label={t("checkout.client.document")}
            placeholder="999.999.999-99"
            inputProps={{
              inputMode: "numeric",
              maxLength: 18,
              "data-cy": "customerDocument",
              "data-testid": "customerDocument",
            }}
            dataCyErrorLabel="customerDocumentError"
            clearErrors={clearErrors}
            validate={(value) =>
              !value || value == "" ? true : Validations.CPF_CNPJ(value)
            }
          />
        )}
      </Stack>
      {values.customFields.map((field, index) => (
        <TextFieldRequired
          key={field.identity}
          control={control}
          fieldName={`customFields.${index}.value`}
          label={field.name}
          placeholder=""
          inputProps={{
            "data-cy": field.identity,
            "data-testid": field.identity,
          }}
          dataCyErrorLabel={field.identity}
          clearErrors={clearErrors}
          validate={(value) => (value ?? "") !== ""}
          onChangeValue={(value) => {
            setValue(`customFields.${index}`, {
              identity: field.identity,
              name: field.name,
              value,
            })
          }}
        />
      ))}
    </Stack>
  )
}

export default CheckoutClient4
